import * as React from 'react';
import FeedBack from './FeedBack';
import * as _ from 'underscore';
import { GameGoNoGoData, GameGoNoGoState } from '../Games.types';
import Image from '../PageElements/Image/Image';
import TextWithReferences from '../PageElements/TextWithReferences/TextWithReferences';
import { useAutoSaveOnPageLeave } from '../../../store/hooks';
import { GameStateDB } from '../../../data/datatypes';
import { useLocation } from 'react-router-dom';
import { getUrlWithTransientStateProperties } from '../Sessions/Session.logic';
import { getGameState, saveGameState } from '../../../services/DBService/DbService';
import checkMarkIcon from '../../../static/icons/icon_check-mark_green.svg';
import dismissIcon from '../../../static/icons/icon_dismiss_red.svg';

type Properties = {
    data: GameGoNoGoData;
    setNextButtonLabel: (label: string | undefined) => void;
};

type GoNogoImage = {
    index: number;
    good: boolean;
};

type GoNogoImageFinished = {
    choiceCorrect: boolean;
    choiceTime: number;
} & GoNogoImage;

const counterDefault = 3;

const GoNogo = (props: Properties) => {
    const { data } = props;

    const path = useLocation().pathname;
    const dbId: `game_${string}` = `game_${getUrlWithTransientStateProperties(path)}`;

    const [step, setStep] = React.useState<'pick' | 'pause' | 'playing' | 'end'>('pick');
    const [selected, setSelected] = React.useState<number[]>([]);
    const [results, setResults] = React.useState<GameGoNoGoState['results']>([{
        avgTime: 0,
        correct: 0,
        created: ''
    }]);
    
    const [finishedImages, setFinishedImages] = React.useState<GoNogoImageFinished[]>([]);
    const [counter, setCounter] = React.useState<number>(counterDefault);
    const [timeoutId, setTimeoutId] = React.useState<ReturnType<typeof setTimeout>>();
    const [clickTimer, setClickTimer] = React.useState<number>(0);
    const [lastChoiceCorrect, setLastChoiceCorrect] = React.useState<boolean>(false);

    const [images, setImages] = React.useState<GoNogoImage[]>([]);

    React.useEffect(() => {
        return () => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    // set next-Button label
    React.useEffect(() => {
        props.setNextButtonLabel('Spiel beenden');
        return () => {
            props.setNextButtonLabel(undefined);
        };
    }, [props.setNextButtonLabel]);

    useAutoSaveOnPageLeave(
        [step, selected, results],
        async () => {
            const gameState: GameStateDB = {
                _id: dbId,
                state: {
                    step: step === 'end' ? 'end' : 'pick',
                    selected,
                    results
                }
            };
            await saveGameState(gameState);
        },
        [path],
        async () => {
            const state = await getGameState(dbId);
            if (state) {
                setStep((state.state as GameGoNoGoState).step);
                setSelected((state.state as GameGoNoGoState).selected);
                setResults((state.state as GameGoNoGoState).results);
            }
        }
    );

    function imageToggleSelected(ev: React.MouseEvent<HTMLElement>, i: number) {
        let newSelected = [...selected];
        let idx = newSelected.indexOf(i);
        if (idx === -1) {
            newSelected.push(i);
        } else {
            newSelected.splice(idx, 1);
        }
        setSelected(newSelected);
    }

    function startPlaying() {
        let newImages: GoNogoImage[] = [];
        selected.map(idx => {
            newImages.push({ good: true, index: idx });
            newImages.push({ good: false, index: idx });
        });
        setImages(_.shuffle(newImages));
        setStep('playing');
    }

    function countdown() {
        if (counter > 0) {
            setCounter(counter - 1);
        } else {
            // setTimeoutId(undefined);
        }
    }

    function swiped(up: boolean, time: number) {
        let img = getActiveImage();
        let newFinishedImages = [...finishedImages];
        const choiceCorrect = up ? !img.good : img.good;
        let newFinishedImage = {
            ...img,
            choiceCorrect,
            choiceTime: time,
        };
        newFinishedImages.push(newFinishedImage);
        let newResults = results;

        // let nextStepAfterSwipe: 'pause' | 'end' = 'pause';
        if (finishedImages.length >= data.exerciseCount - 1) {
            // nextStepAfterSwipe = 'end';
            let result: GameGoNoGoState['results'][0] = {
                correct: 0,
                avgTime: 0,
                created: new Date().toJSON(),
            };
            let avgSum = 0;
            let avgCount = 0;
            _.forEach(newFinishedImages, im => {
                if (im.choiceTime !== 0) {
                    avgCount++;
                    avgSum += im.choiceTime;
                }
                if (im.choiceCorrect === true) {
                    result.correct++;
                }
            });
            result.avgTime = avgSum / avgCount || 0;
            newResults = [result, ...newResults];
        }

        setResults(newResults);
        setLastChoiceCorrect(choiceCorrect);
        setFinishedImages(newFinishedImages);
        nextActiveImage();
        setStep('pause');

        // if (nextStepAfterSwipe === 'pause') {
        setTimeout(() => {
            setTimeoutId(undefined);
            let nextStepAfterPause: 'playing' | 'end' = 'playing';
            if (newFinishedImages.length >= data.exerciseCount) {
                nextStepAfterPause = 'end';
            }
            setStep(nextStepAfterPause);
        }, data.timing);
        // }

    }

    function clicked() {
        console.log('clicked');
        if (timeoutId) {
            clearTimeout(timeoutId);
            // setTimeoutId(undefined);
        }

        let time = Date.now() - clickTimer;
        swiped(false, time);
    }

    function notClicked() {
        console.log('not clicked');
        if (timeoutId) {
            clearTimeout(timeoutId);
            // setTimeoutId(undefined);
        }
        swiped(true, 0);
    }

    function getActiveImage() {
        if (images.length <= 1) {
            let newImages: GoNogoImage[] = [];
            selected.map(idx => {
                newImages.push({ good: true, index: idx });
                newImages.push({ good: false, index: idx });
            });
            setImages(_.shuffle(newImages));
        }
        return images[0];
    }

    function nextActiveImage() {
        let newImages: GoNogoImage[] = [];
        if (images.length > 0) {
            newImages = [...images];
            newImages.splice(0, 1);
            setImages(newImages);
        }
    }

    function reset(keepImages: boolean, ev: any) {
        if (keepImages === true) {
            setStep('playing');
            setFinishedImages([]);
            setCounter(counterDefault);
        } else {
            setStep('pick');
            setFinishedImages([]);
            setSelected([]);
        }
        setTimeoutId(undefined);
    }

    let content: JSX.Element;

    switch (step) {
        case 'pick':
            const displayedImages = data.badImages.map((el, i) => (
                <div
                    className={selected.indexOf(i) !== -1 ? 'image selected' : 'image unselected'}
                    key={'bad_' + i}
                    onClick={(ev: React.MouseEvent<HTMLElement>) => imageToggleSelected(ev, i)}
                >
                    <Image data={{ type: 'image', id: el }} />
                </div>
            ));
            let startButtonLabel = `${selected.length}/${data.pickCount}`;
            if (selected.length === data.pickCount) {
                startButtonLabel += ' - Übung starten!';
            }

            content = <>
                <h1>{data.caption}</h1>
                <div>
                    <TextWithReferences text={data.startDesc} />
                </div>
                <div className="game-pick-images">
                    {displayedImages}
                </div>
                <button
                    className="game-nextbtn primary"
                    onClick={startPlaying}
                    disabled={selected.length !== data.pickCount}
                >
                    {startButtonLabel}
                </button>
            </>;
            break;

        case 'playing':
            if (counter > 0) {
                content = (
                    <div className="game-counter">
                        {counter}
                    </div>
                );
                setTimeout(countdown, 1000);
            } else {
                // setTimeoutId(undefined);
                let img = getActiveImage();
                if (img !== undefined) {
                    // there are images left
                    let imageId = img.good === true ? props.data.goodImages[img.index] : props.data.badImages[img.index];

                    if (!timeoutId) {
                        setTimeoutId(setTimeout(notClicked, data.timing));
                        setClickTimer(Date.now());
                    }
                    content = (
                        <div className="game-image-area">
                            <div
                                className="game-image-click"
                                onClick={clicked}
                            >
                                <Image data={{ type: 'image', id: imageId }} />
                            </div>
                        </div>
                    );
                } else {
                    content = <div>undefined Image</div>;
                }

            }
            break;

        case 'pause':
            if (lastChoiceCorrect) {
                content = <div className="game-image-area">
                    <img
                        src={checkMarkIcon}
                        className="correct"
                    />
                </div>;
            } else {
                content = <div className="game-image-area">
                    <img
                        src={dismissIcon}
                        className="incorrect"
                    />
                </div>;
            }
            break;

        case 'end':
            let result = results[0];
            let feedbackScore = (result.correct / data.exerciseCount) * 100;
            let feedbackImage = (feedbackScore < 25.0) ? 'sad.svg' : ((feedbackScore < 75.0) ? 'neutral.svg' : 'happy.svg');
            let feedbackMessage = (feedbackScore < 25.0) ? 'Trainiere weiter!' : ((feedbackScore < 75.0) ? 'Weiter so!' : 'Klasse Leistung!');
            let feedback = <FeedBack score={feedbackScore} image={feedbackImage} message={feedbackMessage} />;

            content = (
                <div className="game-results">
                    <h1>{data.caption}</h1>
                    {feedback}
                    <p>
                        <strong>Richtig:</strong> {result.correct}<br />
                        <strong>Falsch:</strong> {data.exerciseCount - result.correct}<br />
                        <strong>Durchschnittszeit:</strong> {(result.avgTime / 1000).toFixed(2)} Sekunden
                    </p>
                    <button onClick={reset.bind(this, true)} className="primary">Neu starten</button>
                    <button onClick={reset.bind(this, false)} className="primary">Bilder neu auswählen</button>
                </div>
            );
            break;

        default:
            throw 'ApproachAvoidGame: Unbekannter Spiel-Schritt.';
    }
    return (
        <div className="game gonogo">
            {content}
        </div>
    );
};

export default GoNogo;